import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { trackNavClicked } from '../src/lib/analytics';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 根据语言选择 logo 图片（支持 zh、zh-CN、zh-TW 等中文语言代码）
  const isZh = i18n.language.startsWith('zh');
  const logoSrc = isZh ? '/images/logo/logon_cn.png' : '/images/logo/uhomes.png';
  const logoAlt = isZh ? '异乡好居' : 'uhomes.com';

  // 判断当前激活的视图
  const getActiveView = () => {
    const path = location.pathname;
    if (path === '/life') return 'life';
    if (path === '/offices' || path.startsWith('/cities/')) return 'offices';
    if (path === '/jobs' || path.startsWith('/jobs/')) return 'all-jobs';
    return 'home';
  };

  const activeView = getActiveView();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200" onKeyDown={handleKeyDown}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6 sm:gap-10">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer group"
          >
            <img src={logoSrc} alt={logoAlt} className="h-4 sm:h-5" />
          </Link>
          <div className="hidden md:flex items-center gap-6 sm:gap-8 text-sm text-slate-600">
            <Link
              to="/life"
              onClick={() => trackNavClicked('our_story')}
              className={`transition-colors cursor-pointer ${activeView === 'life' ? 'text-blue-600 font-semibold' : 'hover:text-blue-600'}`}
            >
              {t('nav.ourStory')}
            </Link>
            <Link
              to="/offices"
              onClick={() => trackNavClicked('global_offices')}
              className={`transition-colors cursor-pointer ${activeView === 'offices' ? 'text-blue-600 font-semibold' : 'hover:text-blue-600'}`}
            >
              {t('nav.globalOffices')}
            </Link>
            <Link
              to="/jobs"
              onClick={() => trackNavClicked('careers')}
              className={`transition-colors cursor-pointer ${activeView === 'all-jobs' ? 'text-blue-600 font-semibold' : 'hover:text-blue-600'}`}
            >
              {t('nav.careers')}
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <Link
            to="/jobs"
            className="hidden sm:block text-sm font-semibold bg-blue-600 text-white px-5 py-2 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer"
          >
            {t('nav.careers')}
          </Link>
          <LanguageSwitcher />
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            className="md:hidden p-3 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div id="mobile-menu" className="md:hidden bg-white border-t border-slate-200" role="navigation" aria-label="Main navigation">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/life"
              onClick={() => setMobileMenuOpen(false)}
              className={`block w-full text-left px-4 py-3 rounded-lg text-sm transition-colors cursor-pointer ${activeView === 'life' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-700 hover:bg-slate-50 hover:text-blue-600'}`}
            >
              {t('nav.ourStory')}
            </Link>
            <Link
              to="/offices"
              onClick={() => setMobileMenuOpen(false)}
              className={`block w-full text-left px-4 py-3 rounded-lg text-sm transition-colors cursor-pointer ${activeView === 'offices' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-700 hover:bg-slate-50 hover:text-blue-600'}`}
            >
              {t('nav.globalOffices')}
            </Link>
            <Link
              to="/jobs"
              onClick={() => setMobileMenuOpen(false)}
              className={`block w-full text-left px-4 py-3 rounded-lg text-sm transition-colors cursor-pointer ${activeView === 'all-jobs' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-700 hover:bg-slate-50 hover:text-blue-600'}`}
            >
              {t('nav.careers')}
            </Link>
            <Link
              to="/jobs"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full mt-2 bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors cursor-pointer text-center"
            >
              {t('nav.careers')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
